import type {
  Module,
  ModuleAssessment,
  ModuleAssessmentVersionOverride,
  Programme,
  ProgrammeVersion,
} from "@/types";

export type AssessmentStructureStatus = "shared" | "overridden" | "shared-changed";

export interface EffectiveAssessmentStructure {
  assessments: ModuleAssessment[];
  override?: ModuleAssessmentVersionOverride;
  status: AssessmentStructureStatus;
}

export interface AssessmentOverrideDifference {
  assessmentId: string;
  kind: "added" | "excluded" | "changed" | "inherited";
  fields: string[];
}

function cloneAssessments(assessments: ModuleAssessment[]): ModuleAssessment[] {
  return structuredClone(assessments);
}

function stableValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(stableValue);
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, child]) => [key, stableValue(child)]),
    );
  }
  return value;
}

function valuesMatch(left: unknown, right: unknown): boolean {
  return JSON.stringify(stableValue(left)) === JSON.stringify(stableValue(right));
}

function structuresMatch(left: ModuleAssessment[], right: ModuleAssessment[]): boolean {
  return valuesMatch(left, right);
}

/** Describes how each effective assessment differs from the shared structure. */
export function describeAssessmentOverride(
  sharedAssessments: ModuleAssessment[],
  overrideAssessments: ModuleAssessment[],
): AssessmentOverrideDifference[] {
  const sharedById = new Map(sharedAssessments.map((assessment) => [assessment.id, assessment]));
  const overrideById = new Map(
    overrideAssessments.map((assessment) => [assessment.id, assessment]),
  );
  const differences: AssessmentOverrideDifference[] = [];

  overrideAssessments.forEach((assessment) => {
    const shared = sharedById.get(assessment.id);
    if (!shared) {
      differences.push({ assessmentId: assessment.id, kind: "added", fields: [] });
      return;
    }
    const fields = Array.from(new Set([...Object.keys(shared), ...Object.keys(assessment)]))
      .filter((field) => field !== "id")
      .filter((field) => !valuesMatch(shared[field], assessment[field]))
      .sort((left, right) => left.localeCompare(right));
    differences.push({
      assessmentId: assessment.id,
      kind: fields.length > 0 ? "changed" : "inherited",
      fields,
    });
  });

  sharedAssessments.forEach((assessment) => {
    if (!overrideById.has(assessment.id)) {
      differences.push({ assessmentId: assessment.id, kind: "excluded", fields: [] });
    }
  });

  return differences;
}

/** Restores one assessment field from the shared structure without removing other overrides. */
export function revertAssessmentOverrideField(
  assessment: ModuleAssessment,
  sharedAssessment: ModuleAssessment,
  field: string,
): ModuleAssessment {
  const next = structuredClone(assessment);
  const sharedValue = sharedAssessment[field];
  if (sharedValue === undefined) {
    delete next[field];
  } else {
    next[field] = structuredClone(sharedValue);
  }
  return next;
}

/** Creates an independent version override from the module's current shared assessments. */
export function createAssessmentOverride(module: Module): ModuleAssessmentVersionOverride {
  const shared = module.assessments ?? [];
  return {
    baseSharedAssessments: cloneAssessments(shared),
    assessments: cloneAssessments(shared),
  };
}

/** Resolves the assessment structure a programme version should use for a module. */
export function resolveEffectiveAssessments(
  module: Module,
  version: ProgrammeVersion,
): EffectiveAssessmentStructure {
  const shared = module.assessments ?? [];
  const override = version.assessmentOverrides?.[module.id];

  if (!override) {
    return {
      assessments: shared,
      status: "shared",
    };
  }

  return {
    assessments: override.assessments,
    override,
    status: structuresMatch(shared, override.baseSharedAssessments)
      ? "overridden"
      : "shared-changed",
  };
}

/**
 * Projects a programme onto one delivery version for version-specific outputs.
 *
 * Modules placed in the selected version's stages are retained and their
 * effective assessment structures replace the shared structures. When the
 * selected version has no stage placements yet, all modules are retained so a
 * partial descriptor can still be generated.
 */
export function projectProgrammeAssessmentVersion(
  programme: Programme,
  versionId: string,
): Programme {
  const version = (programme.versions ?? []).find((candidate) => candidate.id === versionId);
  if (!version) {
    return programme;
  }

  const placedModuleIds = new Set(
    (version.stages ?? []).flatMap((stage) =>
      (stage.modules ?? []).map((stageModule) => stageModule.moduleId),
    ),
  );
  const modules = (programme.modules ?? [])
    .filter((module) => placedModuleIds.size === 0 || placedModuleIds.has(module.id))
    .map((module) => ({
      ...module,
      assessments: resolveEffectiveAssessments(module, version).assessments,
    }));

  return {
    ...programme,
    modules,
    versions: [version],
  };
}
